// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Colour for the logo canvas: the theme's tokens arrive as CSS strings
 * (`getComputedStyle`), the canvas wants rgba() strings it can fade and mix.
 * DOM-free.
 */

/** sRGB channels 0..255, alpha 0..1. */
export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

const clampTo = (x: number, max: number) => Math.min(max, Math.max(0, x));

/** One `rgb()` channel: a number 0..255 or a percentage. */
function channel(p: string): number {
  return p.endsWith('%') ? (Number.parseFloat(p) / 100) * 255 : Number.parseFloat(p);
}

/** An alpha: a number 0..1 or a percentage. */
function alpha(p: string): number {
  return p.endsWith('%') ? Number.parseFloat(p) / 100 : Number.parseFloat(p);
}

/**
 * A CSS colour as `getComputedStyle` or a token gives it → rgba, or null when
 * it is not a form this reads: `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`,
 * `rgb(r, g, b)`, `rgba(r, g, b, a)`, `rgb(r g b / a)` (a as 0..1 or %).
 */
export function parseCssColor(value: string): Rgba | null {
  const text = value.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3,8})$/.exec(text);
  if (hex) {
    const h = hex[1];
    if (h.length === 3 || h.length === 4) {
      const [r, g, b, a] = [...h].map((c) => Number.parseInt(c + c, 16));
      return { r, g, b, a: h.length === 4 ? a / 255 : 1 };
    }
    if (h.length === 6 || h.length === 8) {
      const at = (i: number) => Number.parseInt(h.slice(i, i + 2), 16);
      return { r: at(0), g: at(2), b: at(4), a: h.length === 8 ? at(6) / 255 : 1 };
    }
    return null;
  }
  const fn = /^rgba?\(\s*([^)]*)\)$/.exec(text);
  if (!fn) return null;
  const parts = fn[1].split(/[\s,/]+/).filter(Boolean);
  if (parts.length !== 3 && parts.length !== 4) return null;
  const [r, g, b] = parts.slice(0, 3).map(channel);
  const a = parts.length === 4 ? alpha(parts[3]) : 1;
  if (![r, g, b, a].every((x) => Number.isFinite(x))) return null;
  return { r: clampTo(r, 255), g: clampTo(g, 255), b: clampTo(b, 255), a: clampTo(a, 1) };
}

/** WCAG relative luminance, 0 (black) … 1 (white); alpha ignored. */
export function relativeLuminance(c: Rgba): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
}

export type Blend = 'source-over' | 'lighter';

/**
 * How the points composite: additively ('lighter', the reference's glow) when
 * the ink is light — the logo sits on a dark ground — and plainly when the ink
 * is dark, where adding would wash the points out to white.
 */
export function blendFor(ink: Rgba | null): Blend {
  return ink !== null && relativeLuminance(ink) > 0.5 ? 'lighter' : 'source-over';
}

/** `a + (b - a)·t`, per channel (alpha from `a`). */
export function mixRgba(a: Rgba, b: Rgba, t: number): Rgba {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t, a: a.a };
}

/** A canvas fill style for `c` at `alpha` times its own alpha. */
export function rgbaString(c: Rgba, alphaScale = 1): string {
  const a = clampTo(c.a * alphaScale, 1);
  return `rgba(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)},${a.toFixed(3)})`;
}
